import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Search, MapPin } from 'lucide-react';

interface BurialResult {
  id: string;
  deceasedName: string;
  deceasedHebrewName: string | null;
  dateOfDeath: string | null;
  dateOfBurial: string | null;
  hebrewDateOfDeath: string | null;
  location: {
    cemetery: string;
    section: string;
    row: string;
    plot: string;
  };
}

interface FamilyBurial {
  id: string;
  deceasedName: string;
  deceasedHebrewName: string | null;
  dateOfDeath: string | null;
  dateOfBurial: string | null;
  hebrewDateOfDeath: string | null;
  notes: string | null;
  location: {
    cemetery: string;
    section: string;
    row: string;
    plot: string;
  };
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function CemeteryPage() {
  useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ['cemetery-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const response = await api.get<BurialResult[]>(`/cemetery/search?query=${encodeURIComponent(searchQuery)}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  const { data: familyBurials } = useQuery({
    queryKey: ['cemetery-family'],
    queryFn: async () => {
      const response = await api.get<FamilyBurial[]>('/cemetery/my-family');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cemetery Records</h1>
          <p className="mt-2 text-gray-600">
            Search burial records and find resting places of loved ones
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Burial Records
          </label>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter a name to search..."
              className="w-full pl-12 pr-4 py-3 text-base rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Search Results */}
          {searching && (
            <div className="flex items-center justify-center py-6">
              <div className="h-6 w-6 animate-spin rounded-full border-3 border-primary-600 border-t-transparent" />
            </div>
          )}

          {searchQuery.length >= 2 && !searching && searchResults && searchResults.length === 0 && (
            <p className="mt-4 text-center text-gray-500 text-sm">
              No records found for "{searchQuery}"
            </p>
          )}

          {searchResults && searchResults.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100">
              {searchResults.map((burial) => (
                <div key={burial.id} className="py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{burial.deceasedName}</h3>
                      {burial.deceasedHebrewName && (
                        <p className="text-sm text-gray-600">{burial.deceasedHebrewName}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      {burial.location.cemetery} &mdash; {burial.location.section}, Row {burial.location.row}, Plot {burial.location.plot}
                    </p>
                    {burial.dateOfDeath && (
                      <p>Date of Passing: {formatDate(burial.dateOfDeath)}</p>
                    )}
                    {burial.hebrewDateOfDeath && (
                      <p>Hebrew Date: {burial.hebrewDateOfDeath}</p>
                    )}
                    {burial.dateOfBurial && (
                      <p>Date of Burial: {formatDate(burial.dateOfBurial)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Family Burials */}
        {familyBurials && familyBurials.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">My Family</h2>
            <div className="space-y-4">
              {familyBurials.map((burial) => (
                <div
                  key={burial.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
                >
                  <h3 className="font-semibold text-gray-900">{burial.deceasedName}</h3>
                  {burial.deceasedHebrewName && (
                    <p className="text-sm text-gray-600 mt-0.5">{burial.deceasedHebrewName}</p>
                  )}
                  <div className="mt-3 space-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      {burial.location.cemetery} &mdash; {burial.location.section}, Row {burial.location.row}, Plot {burial.location.plot}
                    </p>
                    {burial.dateOfDeath && (
                      <p>Date of Passing: {formatDate(burial.dateOfDeath)}</p>
                    )}
                    {burial.hebrewDateOfDeath && (
                      <p>Hebrew Date: {burial.hebrewDateOfDeath}</p>
                    )}
                    {burial.dateOfBurial && (
                      <p>Date of Burial: {formatDate(burial.dateOfBurial)}</p>
                    )}
                    {burial.notes && (
                      <p className="italic text-gray-400 mt-2">{burial.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Informational Note */}
        <div className="mt-8 bg-stone-50 rounded-xl p-6 border border-stone-200">
          <p className="text-stone-700 text-sm leading-relaxed">
            If you need assistance locating a burial record or have information that should be
            updated, please contact the synagogue office. We maintain these records as a service
            to our community and to honor the memory of those who have passed.
          </p>
        </div>
      </div>
    </div>
  );
}
